import { type _GettersTree, defineStore } from 'pinia';

import type { Alert } from '~/stores/alert/types';

const STORE_ID = 'alerts';

interface Actions {
  add: (alert: Partial<Alert>) => void;
  addError: (alert: Partial<Alert>) => void;
  addSuccess: (alert: Partial<Alert>) => void;
  addWarning: (alert: Partial<Alert>) => void;
  remove: (id: string) => void;
}

interface State {
  alerts: Alert[];
}

export const useAlertStore = defineStore<typeof STORE_ID, State, _GettersTree<State>, Actions>(STORE_ID, {
  actions: {
    add(alert: Partial<Alert>) {
      const id = alert.id ?? crypto.randomUUID();

      if (this.alerts.find((a) => a.id === id)) return;

      this.alerts.push({
        actionClick: alert.actionClick,
        actionLabel: alert.actionLabel,
        closeable: alert.closeable ?? true,
        icon: alert.icon ?? true,
        id,
        shadow: alert.shadow,
        text: alert.text,
        timeout: alert.timeout,
        title: alert.title,
        translate: alert.translate ?? true,
        type: alert.type ?? 'neutral',
      });
    },

    addError(alert: Partial<Alert>) {
      this.add({ ...alert, type: 'error' });
    },

    addSuccess(alert: Partial<Alert>) {
      this.add({ ...alert, type: 'positive' });
    },

    addWarning(alert: Partial<Alert>) {
      this.add({ ...alert, type: 'warning' });
    },

    remove(id: string) {
      this.alerts = this.alerts.filter((a) => a.id !== id);
    },
  },

  state: (): State => ({
    alerts: [],
  }),
});
